/**
 * GovSharePage — 단톡방 공유 (2026-10-04 지도사 · UI/UX 5개사 검토 반영)
 *  최근 「수집」된 공고(검토함에 들어온 때 — 등록한 날 아님) 중 등록했고 지금 신청할 수 있으며 그 방에 아직 안 올린 것을
 *  단톡방(지역 묶음)별 공유문으로 정리 → 「복사」 → 카톡에 붙여넣고 → 「붙여넣었어요 · 다음 방」 한 번 (= 이 방 올림 기록 + 다음 방 글 복사).
 *
 *  설계 원칙
 *   · 보는 순서 = 일하는 순서: 방 목록(할 일) → 이 방 공고(빼기·순서·대상) → 카톡 미리보기 + 큰 버튼 하나
 *   · 상태는 서버 기준(새로고침·다른 PC·카톡 다녀와도 같음) — 오늘 올린 방은 ✓, 마지막 묶음은 「올림 취소」
 *   · 기록은 붙여넣은 뒤에 — 복사만 하고 안 붙인 공고가 「올림」 으로 남지 않게
 *   · 기본 기간 = 「지난 공유 이후」(방마다 마지막으로 올린 뒤 수집된 것) — 주말·휴일 뒤에도 고를 필요 없음
 *   · 공고 내용(기간·대상)이 바뀌면 「내용 바뀜」 으로 다시 후보 · 다른 사람이 먼저 올렸으면 복사 전에 알림
 *   · 용어는 「올리기 / 올림」 하나 · 글자 12/13/15 (다른 탭과 같은 단계 — 2026-10-04 지도사 「큼직큼직」) · 회색 글자 대비 4.5:1 이상
 *   · 대상 문구는 화면에서 입력하지 않는다 — 필요할 때 AI 가 채워 제목 뒤 괄호로 자동으로 붙는다 (2026-10-04 지도사)
 *   · ✕ = 이 방에서 빼기(다시 안 나옴 · 「뺀 공고」에서 되살리기) · 링크는 우리 단축 주소 roum-partners.com/g/…
 *   · 단축키: Ctrl/⌘+Enter = 큰 버튼 · Alt+↑/↓ = 방 이동
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../api';
import { GovTopBar, splitRegions } from '../components/GovFeedParts';

/* ── 이 화면 전용 토큰 (50·60대 가독성) ── */
const K = {
  text: '#111827', sub: '#374151', muted: '#4B5563', faint: '#6B7280', line: '#D1D5DB', lineLt: '#E5E7EB', bg: '#F9FAFB',
  blue: '#2563EB', blueDk: '#1D4ED8', blueLt: '#EFF6FF', blueLine: '#BFDBFE',
  green: '#15803D', greenLt: '#F0FDF4', greenLine: '#BBF7D0',
  amber: '#92400E', amberLt: '#FFFBEB', amberLine: '#F59E0B',
  red: '#B91C1C', redLt: '#FEF2F2', kakao: '#B2C7D9',
};
const FS = { s: 12, m: 13, l: 15 } as const;

const SIDO = ['서울', '인천', '부산', '대구', '광주', '대전', '울산', '세종', '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주'];
const GG = ['북부', '동부', '남부', '서부'];
const GG_HINT: Record<string, string> = {
  북부: '고양·의정부·남양주·파주·구리·포천·양주·동두천·가평·연천',
  동부: '성남·하남·광주·이천·여주·양평',
  남부: '수원·용인·화성·오산·평택·안성',
  서부: '부천·광명·시흥·안산·안양·군포·의왕·과천·김포',
};
const PERIODS: { d: number; label: string; hint: string }[] = [
  { d: 0, label: '지난 공유 이후', hint: '방마다 마지막으로 올린 뒤 수집된 공고 (처음이면 최근 3일)' },
  { d: 1, label: '오늘', hint: '오늘(새벽 포함) 수집된 공고' },
  { d: 2, label: '2일', hint: '어제·오늘 수집된 공고' },
  { d: 3, label: '3일', hint: '그저께·어제·오늘 수집된 공고' },
  { d: 7, label: '7일', hint: '최근 7일 수집된 공고' },
];
const LONG_TEXT = 1500; // 카톡에서 「전체보기」 로 접히기 시작하는 대략의 길이

const kstNow = () => new Date(Date.now() + 9 * 3600 * 1000).toISOString();
const kstDay = () => kstNow().slice(0, 10);
const kstOf = (iso: string) => new Date(new Date(iso).getTime() + 9 * 3600 * 1000).toISOString();
const isToday = (iso: string | null | undefined) => !!iso && kstOf(iso).slice(0, 10) === kstDay();
const hm = (iso: string | null | undefined) => {
  if (!iso) return '';
  const d = kstOf(iso);
  return d.slice(0, 10) === kstDay() ? d.slice(11, 16) : `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))} ${d.slice(11, 16)}`;
};
const collectedLabel = (ymd: string | null) => {
  if (!ymd) return '';
  const n = Math.round((Date.parse(kstDay()) - Date.parse(ymd)) / 86400000);
  return n <= 0 ? '오늘 수집' : n === 1 ? '어제 수집' : n === 2 ? '그저께 수집' : `${Number(ymd.slice(5, 7))}/${Number(ymd.slice(8, 10))} 수집`;
};
const md = (ymd: string) => `${Number(ymd.slice(5, 7))}/${Number(ymd.slice(8, 10))}`;

/** 방별 손댄 것 (이번만 빼기·순서·한 번 더 올리기) — 이 브라우저, 오늘만 (날이 바뀌면 초기화) */
interface RoomState { off: string[]; order: string[]; again: string[] }
const LS_ROOMS = 'gov_share_rooms_v3';
const LS_DAYS = 'gov_share_days_v2';
function lsGet<T>(k: string, d: T): T { try { const v = JSON.parse(localStorage.getItem(k) || 'null'); return v == null ? d : v; } catch { return d; } }
function lsSet(k: string, v: unknown) { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* 저장 불가 — 화면은 그대로 */ } }
function loadRooms(): Record<string, RoomState> { const v = lsGet<{ day?: string; rooms?: Record<string, RoomState> }>(LS_ROOMS, {}); return v.day === kstDay() && v.rooms ? v.rooms : {}; }

async function copyText(text: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(text); return true; } catch { /* 아래 대체 */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
    document.body.appendChild(ta); ta.select();
    const ok = document.execCommand('copy'); document.body.removeChild(ta); return ok;
  } catch { return false; }
}

function useNarrow() {
  const [n, setN] = useState(() => typeof window !== 'undefined' && window.innerWidth < 900);
  useEffect(() => { const f = () => setN(window.innerWidth < 900); window.addEventListener('resize', f); return () => window.removeEventListener('resize', f); }, []);
  return n;
}

/** 지금 클립보드에 들어 있는 글 (아직 「붙여넣었어요」 전) */
interface Pending { room: string; ids: string[]; lines: Record<string, string>; text: string; at: number }

export default function GovSharePage({ topLeft, onGoInbox }: { topLeft: React.ReactNode; onGoInbox?: () => void }) {
  const narrow = useNarrow();
  const [days, setDays] = useState<number>(() => { const v = Number(lsGet(LS_DAYS, 0)); return PERIODS.some(p => p.d === v) ? v : 0; });
  const [data, setData] = useState<api.GovShareData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [roomKey, setRoomKey] = useState('');
  const [rst, setRst] = useState<Record<string, RoomState>>(loadRooms);
  const [draft, setDraft] = useState<Record<string, string>>({});   // 대상 문구 — 화면에 친 값이 곧 복사되는 값
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  const [filling, setFilling] = useState(false);
  const [toast, setToast] = useState<{ msg: string; tone?: 'warn' | 'ok'; act?: { label: string; run: () => void } } | null>(null);
  const [warn, setWarn] = useState('');
  const [openShared, setOpenShared] = useState(false);
  const [openSkipped, setOpenSkipped] = useState(false);
  const [openRemoved, setOpenRemoved] = useState(false);
  const [gone, setGone] = useState<Record<string, string[]>>({});   // 방금 뺀 것 — 서버 응답 전에도 바로 사라지게
  const [mobileTab, setMobileTab] = useState<'preview' | 'items'>('preview');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const autoFilled = useRef(false);
  const chipRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  const load = useCallback(async (d: number) => {
    setLoading(true); setError('');
    try { const r = (await api.getGovShare(d)).data; setData(r); return r; }
    catch (e) { setError(e instanceof Error ? e.message : '불러오지 못했습니다'); return null; }
    finally { setLoading(false); }
  }, []);

  // 기간이 바뀌면 다시 불러오기 · 빈 대상 문구는 한 번 자동으로 채운다(직접 고친 것은 건드리지 않음)
  useEffect(() => {
    lsSet(LS_DAYS, days);
    load(days).then(async (r) => {
      if (!r) return;
      const miss = r.items.filter(it => !it.target).map(it => it.id);
      if (!miss.length || autoFilled.current) return;
      autoFilled.current = true;
      setFilling(true);
      try { const f = (await api.fillGovShareTargets(miss)).data; if (f && f.filled) await load(days); }
      catch { /* 키 없음 등 — 직접 쓰면 된다 */ }
      finally { setFilling(false); }
    });
  }, [days, load]);

  // 카톡 다녀오면(창 다시 보이면) 서버 상태로 다시 맞춘다 — 다른 사람이 올린 것·날짜 바뀜 반영
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') load(days); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [days, load]);

  const byId = useMemo(() => new Map((data?.items || []).map(it => [it.id, it])), [data]);
  const rooms = useMemo(() => data?.rooms || [], [data]);
  const room = rooms.find(r => r.key === roomKey) || null;

  const rs = useCallback((k: string): RoomState => ({ off: [], order: [], again: [], ...(rst[k] || {}) }), [rst]);
  const updRoom = (k: string, fn: (s: RoomState) => RoomState) => setRst(prev => {
    const n = { ...prev, [k]: fn({ off: [], order: [], again: [], ...(prev[k] || {}) }) };
    lsSet(LS_ROOMS, { day: kstDay(), rooms: n }); return n;
  });

  const targetOf = useCallback((id: string) => (draft[id] !== undefined ? draft[id] : (byId.get(id)?.target || '')), [draft, byId]);
  /** 화면 값으로 한 줄 — 대상 문구를 고치는 중이어도 복사되는 글 = 보이는 글 */
  const lineOf = useCallback((id: string) => {
    const it = byId.get(id); if (!it) return '';
    const t = targetOf(id).replace(/\s+/g, ' ').trim().replace(/\s*대상$/, '');
    const tgt = t ? ` (${/추천$/.test(t) ? t : `${t} 대상`})` : '';
    return `${it.title}${tgt} (${it.period})${it.url ? `\n${it.url}` : ''}`;
  }, [byId, targetOf]);

  const candIds = useCallback((r: api.GovShareRoom) => {
    const st = rs(r.key);
    const g = new Set(gone[r.key] || []);
    const extra = r.shared.concat(r.skipped).filter(id => st.again.includes(id));
    const base = r.items.concat(extra).filter(id => !g.has(id));
    const known = st.order.filter(id => base.includes(id));
    return known.concat(base.filter(id => !known.includes(id)));
  }, [rs, gone]);
  const pickIds = useCallback((r: api.GovShareRoom) => { const off = new Set(rs(r.key).off); return candIds(r).filter(id => !off.has(id)); }, [rs, candIds]);
  const textOf = useCallback((r: api.GovShareRoom) => {
    const lines = pickIds(r).map((id, i) => `${i + 1}. ${lineOf(id)}`);
    return lines.length ? `${data?.header || ''}\n\n${lines.join('\n\n')}` : '';
  }, [data, pickIds, lineOf]);

  const hasWork = (r: api.GovShareRoom) => pickIds(r).length > 0;
  const nextRoom = useCallback((from: string) => {
    const i = rooms.findIndex(r => r.key === from);
    for (let k = 1; k < rooms.length; k++) { const r = rooms[(i + k) % rooms.length]; if (pickIds(r).length > 0) return r; }
    return null;
  }, [rooms, pickIds]);

  // 처음·기간 바꿈: 올릴 공고가 있는 첫 방을 고른다 (빈 방에 멈춰 있지 않게)
  useEffect(() => {
    if (!rooms.length) return;
    if (!rooms.some(r => r.key === roomKey)) setRoomKey((rooms.find(r => pickIds(r).length > 0) || rooms[0]).key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rooms]);
  useEffect(() => { setOpenShared(false); setOpenSkipped(false); setOpenRemoved(false); }, [roomKey]);
  useEffect(() => { const el = chipRefs.current[roomKey]; if (narrow && el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' }); }, [roomKey, narrow]);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), toast.act ? 6000 : 3200); return () => clearTimeout(t); }, [toast]);

  /** 대상 문구 고친 것 저장 (복사·기록 전에 반드시) */
  const flushDraft = useCallback(async () => {
    const ent = Object.entries(draft).filter(([id, v]) => (byId.get(id)?.target || '') !== v.replace(/\s+/g, ' ').trim().replace(/\s*대상$/, ''));
    for (const [id, v] of ent) { try { await api.setGovShareTarget(id, v); } catch { /* 화면 값으로 복사는 된다 */ } }
    return ent.length;
  }, [draft, byId]);

  async function copyRoom(r: api.GovShareRoom, fresh?: api.GovShareData | null) {
    // 복사 직전 서버 상태 다시 확인 — 그사이 다른 사람이 같은 방에 올렸으면 멈추고 알린다
    const latest = fresh !== undefined ? fresh : await load(days);
    const lr = latest ? latest.rooms.find(x => x.key === r.key) : r;
    if (latest && lr) {
      const nowShared = pickIds(r).filter(id => lr.shared.includes(id) && !r.shared.includes(id));
      if (nowShared.length) {
        setWarn(`방금 다른 분이 이 방에 ${nowShared.length}건을 올렸습니다 (${lr.lastBy || '다른 사용자'} ${hm(lr.lastShared)}). 목록을 새로 맞췄으니 확인 후 다시 복사하세요.`);
        return false;
      }
    }
    const rr = lr || r;
    const ids = pickIds(rr);
    const t = textOf(rr);
    if (!t) return false;
    const lines = Object.fromEntries(ids.map(id => [id, lineOf(id)]));
    const ok = await copyText(t);
    setPending({ room: rr.key, ids, lines, text: t, at: Date.now() });
    if (!ok) { setWarn('복사하지 못했습니다. 미리보기 글을 길게 눌러 직접 복사한 뒤 「붙여넣었어요」를 눌러 주세요.'); return false; }
    setWarn('');
    return true;
  }

  /** 큰 버튼 하나: 복사 → (카톡에 붙여넣고) → 붙여넣었어요 · 다음 방 복사 */
  async function mainAction() {
    if (!room || busy) return;
    const ae = document.activeElement as HTMLElement | null;
    if (ae && ae.tagName === 'INPUT') ae.blur();
    setBusy(true);
    try {
      await flushDraft();
      if (pending && pending.room === room.key) {
        // 이 방 올림 기록
        const res = (await api.logGovShare(room.key, pending.ids, pending.lines, data?.asOf || null)).data;
        if (res && res.conflicts && res.conflicts.length) setWarn(`주의: ${res.conflicts.length}건은 다른 분(${res.conflicts[0].by || '다른 사용자'})이 ${hm(res.conflicts[0].at)}에 먼저 올렸습니다. 같은 글이 두 번 올라갔는지 카톡방을 확인해 주세요.`);
        updRoom(room.key, s => ({ ...s, off: [], again: [], order: [] }));
        setDraft({});
        setPending(null);
        const fresh = await load(days);
        const order = rooms.map(r => r.key);
        const cur = order.indexOf(room.key);
        const nxt = fresh ? (fresh.rooms.find(x => order.indexOf(x.key) > cur && x.items.length > 0) || fresh.rooms.find(x => x.key !== room.key && x.items.length > 0)) : null;
        if (nxt) {
          setRoomKey(nxt.key);
          const copied = await copyRoom(nxt, fresh);
          setToast({ msg: copied ? `「${room.name}」 올림 ✓ · 「${nxt.name}」 글을 복사했어요 — 붙여넣으세요` : `「${room.name}」 올림 ✓`, tone: 'ok' });
        } else {
          setToast({ msg: `「${room.name}」 올림 ✓ · 오늘 올릴 방을 모두 마쳤어요`, tone: 'ok' });
        }
      } else {
        const copied = await copyRoom(room);
        if (copied) setToast({ msg: `복사했어요 — 카톡 「${room.kakao || room.name}」 방에 붙여넣은 뒤 「붙여넣었어요」를 눌러 주세요` });
      }
    } catch (e) { setWarn(e instanceof Error ? e.message : '처리하지 못했습니다'); }
    finally { setBusy(false); }
  }

  async function undoLast(r: api.GovShareRoom) {
    if (!r.lastBatch) return;
    try {
      const n = (await api.undoGovShare(r.key, r.lastBatch.id)).data?.n || 0;
      if (!n) { setWarn('오늘 올린 기록만 취소할 수 있습니다.'); return; }
      setToast({ msg: `「${r.name}」 ${n}건 올림을 취소했어요 — 다시 올릴 목록으로 돌아갔습니다` });
      await load(days);
    } catch (e) { setWarn(e instanceof Error ? e.message : '취소하지 못했습니다'); }
  }

  function selectRoom(k: string) { setRoomKey(k); setWarn(''); }
  function toggleItem(id: string) { if (!room) return; updRoom(room.key, s => ({ ...s, off: s.off.includes(id) ? s.off.filter(x => x !== id) : s.off.concat(id) })); }
  /** 전체 선택 · 전체 해제 (2026-10-05 지도사 「전체 체크 해제하는 기능 필요」) — 낱개 체크와 같은 off 목록만 바꾼다 (올림 기록·오늘 진행 계산은 그대로) */
  function setAllItems(on: boolean) { if (!room) return; const all = candIds(room); updRoom(room.key, s => ({ ...s, off: on ? [] : all })); }
  function toggleAgain(id: string) { if (!room) return; updRoom(room.key, s => ({ ...s, again: s.again.includes(id) ? s.again.filter(x => x !== id) : s.again.concat(id), off: s.off.filter(x => x !== id) })); }
  function move(id: string, d: -1 | 1) {
    if (!room) return;
    const ids = candIds(room).slice(); const i = ids.indexOf(id); const j = i + d;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    updRoom(room.key, s => ({ ...s, order: ids }));
  }
  /** ✕ 이 방에서 빼기 — 바로 사라지고(낙관적) 서버에 기록 · 토스트에서 되돌리기 */
  async function removeItem(id: string) {
    if (!room) return;
    const rk = room.key; const title = byId.get(id)?.title || '';
    setGone(g => ({ ...g, [rk]: (g[rk] || []).concat(id) }));
    updRoom(rk, s => ({ ...s, off: s.off.filter(x => x !== id), again: s.again.filter(x => x !== id), order: s.order.filter(x => x !== id) }));
    try {
      await api.skipGovShare(rk, [id]);
      setToast({ msg: `뺐어요 — 「${title.length > 24 ? `${title.slice(0, 24)}…` : title}」`, act: { label: '되돌리기', run: () => restoreItem(rk, id) } });
      await load(days);
    } catch (e) { setGone(g => ({ ...g, [rk]: (g[rk] || []).filter(x => x !== id) })); setWarn(e instanceof Error ? e.message : '빼지 못했습니다'); return; }
    setGone(g => ({ ...g, [rk]: (g[rk] || []).filter(x => x !== id) }));
  }
  async function restoreItem(rk: string, id: string) {
    try { await api.skipGovShare(rk, [id], true); setToast({ msg: '다시 올릴 목록으로 돌렸어요' }); await load(days); }
    catch (e) { setWarn(e instanceof Error ? e.message : '되돌리지 못했습니다'); }
  }
  // 단축키: Ctrl/⌘+Enter = 큰 버튼 · Alt+↑/↓ = 방 이동
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (settingsOpen) return;
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); mainAction(); return; }
      if (e.altKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        const i = rooms.findIndex(r => r.key === roomKey);
        const n = rooms[(i + (e.key === 'ArrowDown' ? 1 : rooms.length - 1)) % rooms.length];
        if (n) { e.preventDefault(); selectRoom(n.key); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const per = PERIODS.find(p => p.d === days);
  const doneN = rooms.filter(r => isToday(r.lastShared)).length;
  const todoN = rooms.filter(r => isToday(r.lastShared) || hasWork(r)).length;
  const unassigned = (data?.unassigned || []).map(id => byId.get(id)).filter((x): x is api.GovShareItem => !!x);
  const liveText = room ? textOf(room) : '';
  const isPending = !!(room && pending && pending.room === room.key);
  const text = isPending && pending ? pending.text : liveText;
  const nxt = room ? nextRoom(room.key) : null;
  const n = room ? pickIds(room).length : 0;
  const mainLabel = !room ? '복사하기'
    : isPending ? (nxt ? `✓ 붙여넣었어요 · 다음 「${nxt.name}」 복사` : '✓ 붙여넣었어요 · 끝')
    : n ? `${n}건 복사하기` : '올릴 공고 없음';
  const mainOn = !!room && (isPending || n > 0) && !busy;
  const rangeText = data ? (days === 0 ? '방마다 마지막 올린 뒤' : `${md(data.from)}~${md(data.today)} 수집`) : '';

  /* ── 조각 ── */
  const periodBar = (
    <div role="radiogroup" aria-label="수집 기간" style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, flexWrap: 'wrap' }}>
      <span style={{ fontSize: FS.s, color: K.muted, fontWeight: 700, whiteSpace: 'nowrap' }}>수집 기간</span>
      <div style={{ display: 'inline-flex', padding: 2, gap: 2, borderRadius: 8, background: '#E5E7EB', flexWrap: 'wrap' }}>
        {PERIODS.map(p => {
          const on = p.d === days;
          return <button key={p.d} role="radio" aria-checked={on} title={p.hint} onClick={() => setDays(p.d)}
            style={{ minHeight: 28, padding: '0 10px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: FS.s, fontWeight: on ? 700 : 500, whiteSpace: 'nowrap',
              background: on ? '#fff' : 'transparent', color: on ? K.text : K.muted, boxShadow: on ? '0 1px 2px rgba(17,24,39,.15)' : 'none' }}>{p.label}</button>;
        })}
      </div>
      {data && <span style={{ fontSize: FS.s, color: K.muted, whiteSpace: 'nowrap' }} title={per?.hint}>{rangeText} · 신청 가능 <b style={{ color: K.text }}>{data.total}</b>건</span>}
    </div>
  );

  const progress = (
    <div style={{ padding: narrow ? '0 0 8px' : '2px 2px 10px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: FS.s, color: K.muted, fontWeight: 700 }}>
        <span>오늘 진행</span><span style={{ color: todoN && doneN >= todoN ? K.green : K.text }}>{doneN} / {todoN}곳</span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: K.lineLt, marginTop: 6, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${todoN ? Math.round((doneN / todoN) * 100) : 0}%`, background: K.green, transition: 'width .3s' }} />
      </div>
    </div>
  );

  const roomBtn = (r: api.GovShareRoom) => {
    const act = r.key === roomKey; const c = pickIds(r).length; const done = isToday(r.lastShared);
    const pend = !!(pending && pending.room === r.key);
    const held = c ? 0 : candIds(r).length; // 후보는 있는데 모두 빼둔 상태(전체 해제) — 「올릴 공고 없음」 과 구분
    const sub = pend ? '복사함 · 붙여넣기 전' : c ? `올릴 공고 ${c}건` : done ? `✓ ${hm(r.lastShared)} 올림` : held ? `고른 공고 없음 · ${held}건 빼둠` : '올릴 공고 없음';
    const tone = pend ? K.amber : c ? K.blue : done ? K.green : K.faint;
    const okDone = done && !c;
    return (
      <button key={r.key} ref={el => { chipRefs.current[r.key] = el; }} onClick={() => selectRoom(r.key)} aria-current={act}
        aria-label={`${r.name}, ${sub}`}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', cursor: 'pointer', minHeight: 44, width: '100%', minWidth: 0,
          padding: '6px 10px', borderRadius: 10, boxSizing: 'border-box',
          border: `1.5px solid ${act ? K.blue : okDone ? K.greenLine : K.lineLt}`,
          background: act ? K.blueLt : okDone ? K.greenLt : '#fff',
        }}>
        <span aria-hidden style={{ minWidth: 22, height: 22, padding: '0 5px', boxSizing: 'border-box', borderRadius: 11, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: FS.s, fontWeight: 800, background: okDone ? K.green : c ? K.blue : K.lineLt, color: okDone || c ? '#fff' : K.muted }}>{okDone ? '✓' : c || '–'}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: FS.m, fontWeight: 700, color: K.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.35 }}>{r.name}</span>
          <span style={{ display: 'block', fontSize: FS.s, color: tone, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</span>
        </span>
      </button>
    );
  };

  const notes = (
    <>
      {!!data?.pending && (
        <div style={{ padding: 12, borderRadius: 12, background: K.bg, border: `1px solid ${K.lineLt}` }}>
          <div style={{ fontSize: FS.s, color: K.sub }}>검토함에 아직 등록 안 한 공고 <b style={{ color: K.blue }}>{data.pending}건</b></div>
          {onGoInbox && <button onClick={onGoInbox} style={{ ...linkBtn, marginTop: 4 }}>등록하러 가기 →</button>}
        </div>
      )}
      {unassigned.length > 0 && (
        <div style={{ padding: 12, borderRadius: 12, background: K.amberLt, border: '1px solid #FDE68A' }}>
          <div style={{ fontSize: FS.s, color: K.amber, fontWeight: 700 }}>맡은 방이 없는 공고 {unassigned.length}건 ({Array.from(new Set(unassigned.flatMap(u => splitRegions(u.region)))).join('·') || '-'})</div>
          {unassigned.slice(0, 4).map(it => <div key={it.id} style={{ fontSize: FS.s, color: K.amber, marginTop: 4, lineHeight: 1.4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>· {it.title}</div>)}
          <button onClick={() => setSettingsOpen(true)} style={{ ...linkBtn, color: K.amber, marginTop: 4 }}>방 설정 열기 →</button>
        </div>
      )}
    </>
  );

  const roomList = narrow ? (
    <div style={{ padding: '10px 12px', borderBottom: `1px solid ${K.lineLt}`, flexShrink: 0 }}>
      {progress}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: 6 }}>{rooms.map(roomBtn)}</div>
    </div>
  ) : (
    <div style={{ width: 268, flexShrink: 0, borderRight: `1px solid ${K.lineLt}`, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 8, background: K.bg }}>
      {progress}
      {rooms.map(roomBtn)}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>{notes}</div>
      <button onClick={() => setSettingsOpen(true)} style={{ ...ghostBtn, marginTop: 'auto', alignSelf: 'flex-start' }}>⚙ 방·머리글 설정</button>
    </div>
  );

  const ids = room ? candIds(room) : [];
  const offSet = new Set(room ? rs(room.key).off : []);
  const againSet = new Set(room ? rs(room.key).again : []);
  const shownShared = room ? room.shared.filter(id => !againSet.has(id)) : [];
  const shownSkipped = room ? room.skipped.filter(id => !againSet.has(id)) : [];
  const lastToday = room && room.lastBatch && isToday(room.lastBatch.at) ? room.lastBatch : null;

  const itemCard = (id: string, i: number) => {
    const it = byId.get(id); if (!it || !room) return null;
    const off = offSet.has(id);
    const changed = room.changed.includes(id);
    const again = againSet.has(id);
    const seq = ids.filter(x => !offSet.has(x)).indexOf(id) + 1;
    return (
      <div key={id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '9px 8px 9px 6px', marginBottom: 6, borderRadius: 10,
        border: `1px solid ${off ? K.lineLt : K.line}`, background: off ? K.bg : '#fff' }}>
        <label style={{ display: 'flex', justifyContent: 'center', width: 28, paddingTop: 1, cursor: 'pointer', flexShrink: 0 }}
          title={off ? '이번에 올리기' : '이번에는 빼기 (다음에 다시 보임)'}>
          <input type="checkbox" checked={!off} onChange={() => toggleItem(id)} aria-label={`「${it.title}」 이번 공유문에 넣기`}
            style={{ width: 16, height: 16, margin: 0, accentColor: K.blue, cursor: 'pointer' }} />
        </label>
        <div style={{ flex: 1, minWidth: 0, opacity: off ? 0.55 : 1 }}>
          <div style={{ fontSize: FS.m, fontWeight: 700, color: K.text, lineHeight: 1.45, wordBreak: 'keep-all', overflowWrap: 'anywhere' }}>
            {!off && <span style={{ color: K.blue, marginRight: 4 }}>{seq}.</span>}{it.title}
            {it.target && <span style={{ fontWeight: 400, color: K.sub }}> ({/추천$|대상$/.test(it.target) ? it.target : `${it.target} 대상`})</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, flexWrap: 'wrap', fontSize: FS.s, color: K.faint }}>
            <span style={{ color: K.sub }}>{it.period}</span>
            <span aria-hidden>·</span><span>{collectedLabel(it.collectedOn)}</span>
            {it.daysLeft != null && it.daysLeft <= 3 && <span style={{ ...pill, color: K.red, background: K.redLt, borderColor: '#FECACA' }}>{it.daysLeft === 0 ? '오늘 마감' : `D-${it.daysLeft}`}</span>}
            {changed && <span style={{ ...pill, color: K.amber, background: K.amberLt, borderColor: '#FDE68A' }} title="지난번 올린 뒤 기간 등 공유문이 바뀌었습니다">내용 바뀜</span>}
            {again && <span style={{ ...pill, color: K.sub, background: K.bg, borderColor: K.line }}>↻ 한 번 더</span>}
            {(it.dupCount ?? 1) > 1 && <span style={{ ...pill, color: K.sub, background: K.bg, borderColor: K.line }} title="같은 공고가 여러 건 등록돼 있어 대표 한 줄로 올립니다 (운영기관별 · 시·군별 · 개정 공고) — 링크는 대표 것">같은 공고 {it.dupCount}건</span>}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
          {!off && <>
            <button aria-label={`「${it.title}」 위로`} title="위로" onClick={() => move(id, -1)} disabled={i === 0} style={{ ...iconBtn, visibility: i === 0 ? 'hidden' : 'visible' }}>↑</button>
            <button aria-label={`「${it.title}」 아래로`} title="아래로" onClick={() => move(id, 1)} disabled={i === ids.length - 1} style={{ ...iconBtn, visibility: i === ids.length - 1 ? 'hidden' : 'visible' }}>↓</button>
          </>}
          <button aria-label={`「${it.title}」 이 방에서 빼기`} title="이 방에서 빼기 — 다시 안 나옵니다 (아래 「뺀 공고」에서 되살리기)" onClick={() => removeItem(id)}
            style={{ ...iconBtn, color: K.faint }}>✕</button>
        </div>
      </div>
    );
  };

  const foldList = (title: string, list: string[], open: boolean, setOpen: (v: boolean) => void, meta: (id: string) => string, actLabel: string) => list.length > 0 && (
    <div style={{ marginTop: 10 }}>
      <button onClick={() => setOpen(!open)} aria-expanded={open} style={{ ...ghostBtn, display: 'flex', alignItems: 'center', gap: 6 }}>
        <span aria-hidden>{open ? '▾' : '▸'}</span> {title} {list.length}건
      </button>
      {open && list.map(id => {
        const it = byId.get(id); if (!it) return null;
        return (
          <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', marginTop: 5, borderRadius: 8, background: K.bg, border: `1px solid ${K.lineLt}`, flexWrap: narrow ? 'wrap' : 'nowrap' }}>
            <span style={{ flex: 1, minWidth: 0, fontSize: FS.s, color: K.sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.title}</span>
            <span style={{ fontSize: FS.s, color: K.faint, whiteSpace: 'nowrap' }}>{meta(id)}</span>
            <button onClick={() => toggleAgain(id)} style={ghostBtn}>{actLabel}</button>
          </div>
        );
      })}
    </div>
  );

  const itemList = (
    <div style={{ flex: 1, minWidth: 0, overflowY: narrow ? 'visible' : 'auto', padding: narrow ? 12 : 18 }}>
      {room && (
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: FS.l, fontWeight: 700, color: K.text }}>{room.name}</h3>
          {room.kakao && <span style={{ fontSize: FS.s, color: K.sub }}>카톡방 「{room.kakao}」</span>}
          <span style={{ fontSize: FS.s, color: K.faint }}>{[...room.regions, ...room.gg.map(g => `경기${g}`)].join('·')} + 전국</span>
          <span style={{ flex: 1 }} />
          {filling && <span style={{ fontSize: FS.s, color: K.faint }}>대상 문구 정리 중…</span>}
        </div>
      )}
      {room && lastToday && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', marginBottom: 10, borderRadius: 12, background: K.greenLt, border: `1px solid ${K.greenLine}`, flexWrap: 'wrap' }}>
          <span style={{ fontSize: FS.s, fontWeight: 700, color: K.green }}>✓ {hm(lastToday.at)} 이 방에 {lastToday.n}건 올림{room.lastBy ? ` · ${room.lastBy}` : ''}</span>
          <span style={{ flex: 1 }} />
          <button onClick={() => undoLast(room)} style={ghostBtn} title="잘못 올렸거나 붙여넣지 못했으면 기록을 지워 다시 올릴 목록으로 돌립니다 (오늘 기록만)">올림 취소</button>
        </div>
      )}
      {room && ids.length === 0 && (
        <div style={{ padding: '36px 16px', textAlign: 'center', border: `1px dashed ${K.line}`, borderRadius: 12 }}>
          <div style={{ fontSize: 24, color: lastToday ? K.green : K.line }} aria-hidden>{lastToday ? '✓' : '○'}</div>
          <div style={{ fontSize: FS.m, fontWeight: 700, color: K.text, marginTop: 6 }}>{lastToday ? '이 방은 다 올렸어요' : '이 방에 올릴 새 공고가 없습니다'}</div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12, flexWrap: 'wrap' }}>
            {nxt && <button onClick={() => selectRoom(nxt.key)} style={primarySm}>다음 방: {nxt.name} ({pickIds(nxt).length}건) →</button>}
            {days !== 7 && <button onClick={() => setDays(7)} style={ghostBtn}>최근 7일로 넓혀 보기</button>}
          </div>
        </div>
      )}
      {room && ids.length > 0 && <SelectAllBar total={ids.length} picked={n} onChange={setAllItems} />}
      {ids.map(itemCard)}
      {room && foldList('지난번에 안 올린 공고', shownSkipped, openSkipped, setOpenSkipped, id => collectedLabel(byId.get(id)?.collectedOn || null), '이번에 올리기')}
      {room && foldList('이미 올린 공고', shownShared, openShared, setOpenShared, id => `${hm(byId.get(id)?.sharedIn[room.key])} 올림`, '한 번 더 올리기')}
      {room && (room.removed || []).length > 0 && (
        <div style={{ marginTop: 10 }}>
          <button onClick={() => setOpenRemoved(!openRemoved)} aria-expanded={openRemoved} style={{ ...ghostBtn, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span aria-hidden>{openRemoved ? '▾' : '▸'}</span> 뺀 공고 {(room.removed || []).length}건
          </button>
          {openRemoved && (room.removed || []).map(id => {
            const it = byId.get(id); if (!it) return null;
            return (
              <div key={id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', marginTop: 5, borderRadius: 8, background: K.bg, border: `1px solid ${K.lineLt}` }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: FS.s, color: K.faint, textDecoration: 'line-through', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.title}</span>
                <button onClick={() => restoreItem(room.key, id)} style={ghostBtn}>되살리기</button>
              </div>
            );
          })}
        </div>
      )}
      {narrow && <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 14 }}>{notes}</div>}
    </div>
  );

  const chars = text.length;
  const cnt = isPending && pending ? pending.ids.length : n;
  const previewBox = (
    <div style={{ padding: narrow ? 12 : 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: FS.s, color: '#1F2937', fontWeight: 700 }}>{isPending ? '복사한 글 (붙여넣기 전)' : '카톡 미리보기'}</span>
        <span style={{ flex: 1 }} />
        {text && <span style={{ fontSize: FS.s, color: chars > LONG_TEXT ? K.red : '#1F2937', fontWeight: chars > LONG_TEXT ? 700 : 400 }} title={chars > LONG_TEXT ? '카톡에서 「전체보기」로 접힐 수 있어요 — 공고를 몇 건 빼 보세요' : ''}>{cnt}건 · {chars.toLocaleString()}자{chars > LONG_TEXT ? ' · 길어요' : ''}</span>}
      </div>
      {text ? (
        <div style={{ background: '#fff', borderRadius: '4px 14px 14px 14px', padding: '10px 12px', fontSize: FS.m, lineHeight: 1.6, color: '#111', whiteSpace: 'pre-wrap', wordBreak: 'keep-all', overflowWrap: 'anywhere', boxShadow: '0 1px 1px rgba(0,0,0,.08)', userSelect: 'text' }}>
          {text.split(/(https?:\/\/\S+)/).map((p, i) => /^https?:\/\//.test(p) ? <span key={i} style={{ color: K.blueDk, textDecoration: 'underline' }}>{p}</span> : <span key={i}>{p}</span>)}
        </div>
      ) : <div style={{ fontSize: FS.s, color: '#1F2937', padding: 12 }}>올릴 공고가 없습니다</div>}
    </div>
  );
  const actionBar = (
    <div style={narrow
      ? { position: 'fixed', left: 0, right: 0, bottom: 0, padding: '10px 12px calc(10px + env(safe-area-inset-bottom))', background: 'rgba(255,255,255,0.97)', borderTop: `1px solid ${K.lineLt}`, zIndex: 20 }
      : { padding: 14, background: '#fff', borderTop: `1px solid ${K.lineLt}` }}>
      {isPending && room && <div style={{ fontSize: FS.s, color: K.amber, fontWeight: 700, marginBottom: 8, textAlign: 'center' }}>카톡 「{room.kakao || room.name}」 방에 붙여넣으셨나요?</div>}
      <button onClick={mainAction} disabled={!mainOn} title="Ctrl+Enter"
        style={{ width: '100%', minHeight: 44, borderRadius: 10, border: 'none', cursor: mainOn ? 'pointer' : 'default', fontSize: FS.m + 1, fontWeight: 700,
          background: !mainOn ? '#E5E7EB' : isPending ? K.green : K.blue, color: !mainOn ? K.faint : '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '0 12px' }}>
        {busy ? '처리 중…' : mainLabel}
      </button>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 16, marginTop: 4, fontSize: FS.s, minHeight: 24, alignItems: 'center' }}>
        {isPending && room && <button onClick={() => copyRoom(room, data)} style={linkBtn}>같은 글 다시 복사</button>}
        {isPending && <button onClick={() => setPending(null)} style={{ ...linkBtn, color: K.muted }}>안 붙였어요(취소)</button>}
        {!isPending && !narrow && <span style={{ color: K.faint }}>복사 → 카톡에 붙여넣기 → 「붙여넣었어요」 · Ctrl+Enter</span>}
      </div>
    </div>
  );
  const preview = narrow ? (
    <div>{previewBox}</div>
  ) : (
    <div style={{ width: 420, flexShrink: 0, borderLeft: `1px solid ${K.lineLt}`, display: 'flex', flexDirection: 'column', background: K.kakao }}>
      <div style={{ flex: 1, overflowY: 'auto' }}>{previewBox}</div>
      {actionBar}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, background: '#fff' }}>
      <GovTopBar left={topLeft}>
        {!narrow && <div style={{ marginLeft: 12, minWidth: 0 }}>{periodBar}</div>}
        <span style={{ flex: 1 }} />
        {narrow && <button onClick={() => setSettingsOpen(true)} style={{ ...ghostBtn, minWidth: 44 }} aria-label="방·머리글 설정">⚙</button>}
      </GovTopBar>
      {narrow && <div style={{ padding: '8px 12px', borderBottom: `1px solid ${K.lineLt}` }}>{periodBar}</div>}
      {warn && (
        <div role="alert" style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 16px', background: K.redLt, borderBottom: '1px solid #FECACA', color: K.red, fontSize: FS.s, fontWeight: 700 }}>
          <span style={{ flex: 1 }}>⚠ {warn}</span><button onClick={() => setWarn('')} style={{ ...linkBtn, color: K.red, padding: 0 }} aria-label="알림 닫기">닫기</button>
        </div>
      )}
      {error && <div style={{ padding: 12, color: K.red, fontSize: FS.s }}>{error}</div>}
      {loading && !data ? <div style={{ padding: 48, textAlign: 'center', fontSize: FS.m, color: K.muted }}>불러오는 중…</div> : narrow ? (
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingBottom: 150 }}>
          {roomList}
          <div role="tablist" style={{ display: 'flex', gap: 6, padding: '10px 12px 0' }}>
            {(['preview', 'items'] as const).map(t => (
              <button key={t} role="tab" aria-selected={mobileTab === t} onClick={() => setMobileTab(t)}
                style={{ flex: 1, minHeight: 38, borderRadius: 8, border: `1.5px solid ${mobileTab === t ? K.blue : K.lineLt}`, background: mobileTab === t ? K.blueLt : '#fff', color: mobileTab === t ? K.blueDk : K.muted, fontSize: FS.m, fontWeight: 700, cursor: 'pointer' }}>
                {t === 'preview' ? '카톡 미리보기' : `공고 고르기 (${ids.length})`}
              </button>
            ))}
          </div>
          {mobileTab === 'preview' ? <div style={{ background: K.kakao, margin: 12, borderRadius: 12 }}>{preview}</div> : itemList}
          {actionBar}
        </div>
      ) : (
        <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          {roomList}
          {itemList}
          {preview}
        </div>
      )}
      {toast && (
        <div role="status" style={{ position: 'fixed', left: '50%', bottom: narrow ? 160 : 28, transform: 'translateX(-50%)', background: toast.tone === 'ok' ? K.green : '#111827', color: '#fff', padding: '10px 16px', borderRadius: 999, fontSize: FS.s, fontWeight: 600, zIndex: 50, boxShadow: '0 6px 20px rgba(0,0,0,.2)', maxWidth: '92vw', textAlign: 'center' }}>
          {toast.msg}
          {toast.act && <button onClick={() => { const a = toast.act; setToast(null); if (a) a.run(); }} style={{ marginLeft: 12, border: 'none', background: 'transparent', color: '#93C5FD', fontWeight: 700, fontSize: FS.s, cursor: 'pointer', padding: 0 }}>{toast.act.label}</button>}
        </div>
      )}
      {settingsOpen && data && <ShareSettings data={data} onClose={() => setSettingsOpen(false)} onSaved={() => { setSettingsOpen(false); load(days); }} />}
    </div>
  );
}

/** ⚙ 방·머리글 설정 — 방 이름·카톡방 이름·맡는 지역(시·도, 경기 권역) · 머리글 */
function ShareSettings({ data, onClose, onSaved }: { data: api.GovShareData; onClose: () => void; onSaved: () => void }) {
  const [header, setHeader] = useState(data.headerTpl);
  const [rooms, setRooms] = useState(() => data.rooms.map(r => ({ key: r.key, name: r.name, kakao: r.kakao || '', regions: r.regions.slice(), gg: r.gg.slice() })));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const dirty = useRef(false);
  const first = useRef<HTMLInputElement | null>(null);
  useEffect(() => { first.current?.focus(); }, []);
  const tryClose = useCallback(() => { if (!dirty.current || window.confirm('바꾼 내용을 저장하지 않고 닫을까요?')) onClose(); }, [onClose]);
  useEffect(() => { const f = (e: KeyboardEvent) => { if (e.key === 'Escape') tryClose(); }; window.addEventListener('keydown', f); return () => window.removeEventListener('keydown', f); }, [tryClose]);
  const upd = (i: number, f: (r: typeof rooms[number]) => typeof rooms[number]) => { dirty.current = true; setRooms(rs => rs.map((r, j) => (j === i ? f(r) : r))); };
  const tog = (arr: string[], x: string) => (arr.includes(x) ? arr.filter(y => y !== x) : arr.concat(x));
  const covered = new Set(rooms.flatMap(r => r.regions));
  const uncovered = SIDO.filter(s => !covered.has(s));
  const preview = header.replace('{날짜}', md(kstDay()));
  async function save() {
    const empty = rooms.filter(r => !r.name.trim()).length;
    if (empty && !window.confirm(`이름이 빈 방 ${empty}곳은 저장하지 않습니다. 계속할까요?`)) return;
    setBusy(true); setErr('');
    try { await api.saveGovShareSettings({ header, rooms: rooms.filter(r => r.name.trim()) }); onSaved(); }
    catch (e) { setErr(e instanceof Error ? e.message : '저장하지 못했습니다'); }
    finally { setBusy(false); }
  }
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.45)' }} onClick={tryClose} />
      <div role="dialog" aria-modal="true" aria-labelledby="gs-set-title" style={{ position: 'relative', width: 'min(760px, 100%)', maxHeight: '92vh', display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', borderBottom: `1px solid ${K.lineLt}` }}>
          <h3 id="gs-set-title" style={{ margin: 0, fontSize: FS.l, fontWeight: 800, color: K.text }}>단톡방·머리글 설정</h3><span style={{ flex: 1 }} />
          <button onClick={tryClose} style={{ ...ghostBtn, minWidth: 44 }} aria-label="닫기">✕</button>
        </div>
        <div style={{ overflowY: 'auto', padding: 20 }}>
          <label htmlFor="gs-header" style={{ display: 'block', fontSize: FS.s, fontWeight: 700, color: K.text, marginBottom: 6 }}>머리글</label>
          <input id="gs-header" ref={first} value={header} onChange={e => { dirty.current = true; setHeader(e.target.value); }} maxLength={120}
            style={{ width: '100%', boxSizing: 'border-box', height: 44, padding: '0 12px', borderRadius: 10, border: `1px solid ${K.line}`, fontSize: FS.m }} />
          <div style={{ fontSize: FS.s, color: K.muted, marginTop: 6 }}>오늘이면 → <b style={{ color: K.text }}>{preview}</b> <span style={{ color: K.faint }}>({'{날짜}'} 자리에 날짜가 들어갑니다)</span></div>
          <div style={{ fontSize: FS.s, fontWeight: 700, color: K.text, margin: '18px 0 8px' }}>방마다 맡는 지역 <span style={{ fontWeight: 400, color: K.muted }}>— 전국 공고는 모든 방에 들어갑니다</span></div>
          {rooms.map((r, i) => (
            <div key={r.key} style={{ border: `1px solid ${K.lineLt}`, borderRadius: 12, padding: 12, marginBottom: 10 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: FS.s, fontWeight: 800, color: K.faint, width: 18 }}>{i + 1}</span>
                <input value={r.name} onChange={e => upd(i, x => ({ ...x, name: e.target.value }))} maxLength={40} placeholder="방 이름 (화면용)" aria-label={`${i + 1}번 방 이름`}
                  style={{ flex: '1 1 200px', height: 40, padding: '0 10px', borderRadius: 8, border: `1px solid ${K.line}`, fontSize: FS.s, fontWeight: 700 }} />
                <input value={r.kakao} onChange={e => upd(i, x => ({ ...x, kakao: e.target.value }))} maxLength={40} placeholder="실제 카톡방 이름 (선택)" aria-label={`${i + 1}번 카톡방 이름`}
                  style={{ flex: '1 1 200px', height: 40, padding: '0 10px', borderRadius: 8, border: `1px solid ${K.line}`, fontSize: FS.s }} />
                <button onClick={() => { if (window.confirm(`「${r.name || `${i + 1}번 방`}」을 지울까요?`)) { dirty.current = true; setRooms(rs => rs.filter((_, j) => j !== i)); } }} style={{ ...ghostBtn, color: K.red }}>삭제</button>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                {SIDO.map(s => <Chip key={s} on={r.regions.includes(s)} onClick={() => upd(i, x => ({ ...x, regions: tog(x.regions, s) }))}>{s}</Chip>)}
                {GG.map(g => <Chip key={g} on={r.gg.includes(g)} title={GG_HINT[g]} onClick={() => upd(i, x => ({ ...x, gg: tog(x.gg, g) }))}>경기{g}</Chip>)}
              </div>
            </div>
          ))}
          <button onClick={() => { dirty.current = true; setRooms(rs => rs.concat({ key: `r${Date.now().toString(36)}`, name: '', kakao: '', regions: [], gg: [] })); }} style={ghostBtn}>+ 방 추가</button>
          {uncovered.length > 0 && <div style={{ fontSize: FS.s, color: K.amber, marginTop: 12 }}>맡는 방이 없는 지역: {uncovered.join('·')} — 이 지역 공고는 「맡은 방이 없는 공고」로 따로 보입니다</div>}
          <div style={{ fontSize: FS.s, color: K.faint, marginTop: 8, lineHeight: 1.5 }}>경기 권역 — {GG.map(g => `${g}: ${GG_HINT[g]}`).join(' / ')} · 시·군이 없는 경기 공고는 경기 권역을 맡은 모든 방에 들어갑니다</div>
          {err && <div style={{ color: K.red, fontSize: FS.s, marginTop: 10 }}>{err}</div>}
        </div>
        <div style={{ display: 'flex', gap: 8, padding: '12px 20px calc(12px + env(safe-area-inset-bottom))', borderTop: `1px solid ${K.lineLt}` }}>
          <button onClick={tryClose} style={{ flex: 1, minHeight: 48, borderRadius: 12, border: `1px solid ${K.line}`, background: '#fff', fontWeight: 700, fontSize: FS.m, cursor: 'pointer', color: K.sub }}>취소</button>
          <button onClick={save} disabled={busy} style={{ flex: 2, minHeight: 48, borderRadius: 12, border: 'none', background: K.blue, color: '#fff', fontWeight: 800, fontSize: FS.m, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>{busy ? '저장 중…' : '저장'}</button>
        </div>
      </div>
    </div>
  );
}

/** 이 방 목록 머리 — 세 상태 체크박스(모두 · 일부 · 없음) + 「전체 선택 ↔ 전체 해제」 + 「선택 N / 전체 M」 (2026-10-05 지도사 「전체 체크 해제하는 기능 필요」)
 *  체크박스는 낱개 카드의 체크박스와 같은 칸(28px)에 맞춰 표 머리처럼 보인다 · 일부 선택(−)·모두 선택이면 누르면 전체 해제, 없음이면 전체 선택
 *  네이티브 체크박스라 Tab → Space 로 쓸 수 있다 · 미리보기·「N건 복사하기」 는 체크된 것만 센다 */
function SelectAllBar({ total, picked, onChange }: { total: number; picked: number; onChange: (on: boolean) => void }) {
  const ref = useRef<HTMLInputElement | null>(null);
  const all = total > 0 && picked >= total;
  const some = picked > 0 && picked < total;
  useEffect(() => { if (ref.current) ref.current.indeterminate = some; }, [some]);
  const clearing = all || some;
  const label = clearing ? '전체 해제' : '전체 선택';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', minHeight: 32, padding: '0 8px 0 7px', marginBottom: 6 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', minHeight: 32 }}
        title={clearing ? '모두 빼기 — 올릴 것만 다시 체크하세요 (이번만 · 다음에 다시 보임)' : '모두 이번 공유문에 넣기'}>
        <span style={{ display: 'flex', justifyContent: 'center', width: 28, flexShrink: 0 }}>
          <input ref={ref} type="checkbox" checked={all} aria-checked={some ? 'mixed' : all} onChange={() => onChange(!clearing)}
            aria-label={`${label} — 선택 ${picked}건, 전체 ${total}건`}
            style={{ width: 16, height: 16, margin: 0, accentColor: K.blue, cursor: 'pointer' }} />
        </span>
        <span style={{ fontSize: FS.s, fontWeight: 700, color: K.sub, whiteSpace: 'nowrap' }}>{label}</span>
      </label>
      <span style={{ fontSize: FS.s, color: K.faint, whiteSpace: 'nowrap' }}>선택 <b style={{ color: K.text }}>{picked}</b> / 전체 {total}</span>
    </div>
  );
}

function Chip({ on, onClick, children, title }: { on: boolean; onClick: () => void; children: React.ReactNode; title?: string }) {
  return <button onClick={onClick} title={title} aria-pressed={on} style={{ minHeight: 30, padding: '0 10px', borderRadius: 999, fontSize: FS.s, fontWeight: 700, cursor: 'pointer', border: `1.5px solid ${on ? K.blue : K.line}`, background: on ? K.blue : '#fff', color: on ? '#fff' : K.sub }}>{children}</button>;
}

const ghostBtn: React.CSSProperties = { minHeight: 30, padding: '0 10px', borderRadius: 8, border: `1px solid ${K.line}`, background: '#fff', cursor: 'pointer', fontSize: FS.s, fontWeight: 700, color: K.sub, whiteSpace: 'nowrap' };
const primarySm: React.CSSProperties = { minHeight: 32, padding: '0 14px', borderRadius: 8, border: 'none', background: K.blue, color: '#fff', cursor: 'pointer', fontSize: FS.s, fontWeight: 700 };
const linkBtn: React.CSSProperties = { border: 'none', background: 'transparent', padding: '6px 0', cursor: 'pointer', fontSize: FS.s, fontWeight: 700, color: K.blueDk };
const iconBtn: React.CSSProperties = { width: 28, height: 28, padding: 0, borderRadius: 6, border: `1px solid ${K.lineLt}`, background: '#fff', cursor: 'pointer', fontSize: FS.s, color: K.sub, lineHeight: 1 };
const pill: React.CSSProperties = { fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 5, border: '1px solid transparent', whiteSpace: 'nowrap' };
